/**
 * Shared bracelet designer utilities (catalog, canvas, cart).
 */
(function (global) {
  const CONFIG = () => global.BraceletDesignerConfig || {};
  const BEADS = () => global.__BEADS__ || [];
  const TEMPLATES = () => TEMPLATES_LIST();

  function getBeadMap() {
    const map = new Map();
    for (const b of BEADS()) {
      if (b.enabled === false) continue;
      map.set(b.handle, b);
    }
    return map;
  }

  function resolveBead(handle) {
    return getBeadMap().get(handle) || null;
  }

  /** Normalize legacy or compact design JSON from templates / cart properties */
  function normalizeDesignJson(designJson) {
    if (!designJson) return null;
    let raw = designJson;
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch {
        return null;
      }
    }
    const wrist = raw.wrist_size_cm ?? raw.w;
    let handles = [];
    if (Array.isArray(raw.b)) {
      for (const item of raw.b) {
        if (typeof item === 'string') handles.push(item);
        else if (item?.h) handles.push(item.h);
        else if (item?.handle) handles.push(item.handle);
      }
    } else {
      handles = (raw.beads || [])
        .map((item) => (typeof item === 'string' ? item : item?.handle))
        .filter(Boolean);
    }
    return {
      wrist_size_cm: wrist,
      beads: handles.map((handle) => ({ handle })),
    };
  }

  function parseRawDesignJson(designJson) {
    if (!designJson) return null;
    if (typeof designJson === 'object') return designJson;
    try {
      return JSON.parse(designJson);
    } catch {
      return null;
    }
  }

  function diameterFromHandle(handle) {
    const s = String(handle || '');
    const m =
      s.match(/(\d+(?:\.\d+)?)\s*mm/i) ||
      s.match(/-(\d+(?:\.\d+)?)-mm$/i) ||
      s.match(/-(\d+(?:\.\d+)?)mm$/i);
    if (!m) return 8;
    const n = Number(m[1]);
    return Number.isFinite(n) && n > 0 ? n : 8;
  }

  function humanizeHandle(handle) {
    return String(handle || 'Bead')
      .replace(/-+/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function normalizeCategories(category) {
    if (!category) return ['all'];
    if (Array.isArray(category)) return category.map(String);
    const s = String(category).trim();
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      /* plain */
    }
    return [s];
  }

  function keywordsFromHandle(handle) {
    return String(handle || '')
      .toLowerCase()
      .replace(/-(\d+(?:\.\d+)?)-?mm$/i, '')
      .replace(/-mm$/i, '')
      .replace(/-+/g, ' ')
      .trim();
  }

  function categoryHintFromHandle(handle) {
    const norm = String(handle || '').toLowerCase();
    const rules = [
      ['spacer', 'spacer'],
      ['zirconia', 'spacer'],
      ['cross', 'spacer'],
      ['pearl', 'pearl'],
      ['moonstone', 'blue'],
      ['lapis', 'blue'],
      ['amethyst', 'purple'],
      ['rose', 'pink'],
      ['quartz', 'pink'],
      ['aventurine', 'green'],
      ['prehnite', 'green'],
      ['tiger', 'earth'],
      ['obsidian', 'black'],
      ['onyx', 'black'],
      ['milky', 'white_clear'],
      ['snowflake', 'white_clear'],
      ['chalcedony', 'white_clear'],
      ['white', 'white_clear'],
      ['clear', 'white_clear'],
      ['crystal', 'white_clear'],
    ];
    for (const [kw, cat] of rules) {
      if (norm.includes(kw)) return cat;
    }
    return null;
  }

  function catalogBeads() {
    return BEADS().filter((b) => b.enabled !== false);
  }

  /** Fuzzy match when cart JSON handle differs slightly from catalog (e.g. 6-mm vs 6mm). */
  function resolveBeadLoose(handle) {
    const direct = resolveBead(handle);
    if (direct) return direct;
    const norm = String(handle || '')
      .toLowerCase()
      .replace(/-+/g, '-')
      .replace(/-mm$/i, '');
    if (!norm) return null;

    const catalog = catalogBeads();
    const diam = diameterFromHandle(handle);
    const nameQuery = keywordsFromHandle(handle);

    const looseHandle = catalog.find((b) => {
      const bh = String(b.handle || '')
        .toLowerCase()
        .replace(/-+/g, '-')
        .replace(/-mm$/i, '');
      return bh === norm || bh.includes(norm) || norm.includes(bh);
    });
    if (looseHandle) return looseHandle;

    const tagHit = catalog.find((b) => {
      const tags = String(b.tags || '').toLowerCase();
      const code = norm.replace(/-/g, '');
      return (
        tags.includes(norm) ||
        tags.includes(nameQuery) ||
        (code.length >= 3 && tags.replace(/[^a-z0-9]/g, '').includes(code))
      );
    });
    if (tagHit) return tagHit;

    const words = nameQuery.split(/\s+/).filter((w) => w.length > 2);
    const nameHit = catalog.find((b) => {
      if (Math.abs(Number(b.diameter_mm) - diam) > 0.6) return false;
      const n = String(b.name || '').toLowerCase();
      if (n.includes(nameQuery)) return true;
      return words.length > 0 && words.every((w) => n.includes(w));
    });
    if (nameHit) return nameHit;

    return null;
  }

  /**
   * When a template references a bead that is missing or Draft, pick a real catalog bead
   * (same name/category/diameter when possible) instead of a grey stub.
   */
  function findReplacementBead(handle, slotIndex = 0) {
    const loose = resolveBeadLoose(handle);
    if (loose) return loose;

    const catalog = catalogBeads();
    if (!catalog.length) return null;

    const diam = diameterFromHandle(handle);
    const cat = categoryHintFromHandle(handle);
    const nameQuery = keywordsFromHandle(handle);
    const words = nameQuery.split(/\s+/).filter((w) => w.length > 2);

    const sameDiam = (b) => Math.abs(Number(b.diameter_mm) - diam) < 0.6;
    const nameMatch = (b) => {
      const n = String(b.name || '').toLowerCase();
      if (n.includes(nameQuery)) return true;
      return words.length > 0 && words.every((w) => n.includes(w));
    };
    const catMatch = (b) => {
      if (!cat) return true;
      const cats = normalizeCategories(b.category);
      return cats.includes(cat) || cats.includes('all');
    };

    let candidates = catalog.filter((b) => sameDiam(b) && nameMatch(b));
    if (!candidates.length) candidates = catalog.filter((b) => sameDiam(b) && catMatch(b));
    if (!candidates.length) candidates = catalog.filter((b) => catMatch(b) && sameDiam(b));
    if (!candidates.length) candidates = catalog.filter(sameDiam);
    if (!candidates.length) candidates = catalog;

    const idx = Math.abs(slotIndex) % candidates.length;
    return candidates[idx];
  }

  /**
   * Effect images from Metaobject `image_base` (list) — canvas random display.
   * actual_photos are real product photos only (viewer sheet).
   */
  function beadEffectImagePool(bead) {
    if (!bead) return [];
    const fromImages = Array.isArray(bead.images)
      ? bead.images.filter((u) => typeof u === 'string' && u.trim())
      : [];
    if (fromImages.length) return fromImages;
    const main = String(bead.image_url || '').trim();
    return main ? [main] : [];
  }

  function beadDisplayImagePool(bead) {
    return beadEffectImagePool(bead);
  }

  function resolveBeadThumbUrl(bead) {
    if (!bead) return '';
    const thumb = String(bead.thumb_url || '').trim();
    if (thumb) return thumb;
    const imageUrl = String(bead.image_url || '').trim();
    if (imageUrl) return imageUrl;
    if (Array.isArray(bead.images)) {
      const first = bead.images.find((u) => typeof u === 'string' && u.trim());
      if (first) return first.trim();
    }
    return '';
  }

  function pickBeadDisplayImageUrl(bead) {
    const pool = beadDisplayImagePool(bead);
    if (pool.length <= 1) return pool[0] || String(bead?.image_url || '').trim();
    return pool[Math.floor(Math.random() * pool.length)];
  }

  /** Clone catalog bead with a random image when multiple assets exist. */
  function beadWithRandomDisplayImage(bead) {
    if (!bead) return bead;
    if (beadDisplayImagePool(bead).length <= 1) return { ...bead };
    return { ...bead, image_url: pickBeadDisplayImageUrl(bead) };
  }

  function beadImageUrlsForPreload(beads) {
    const seen = new Set();
    const urls = [];
    const add = (u) => {
      const s = String(u || '').trim();
      if (!s || seen.has(s)) return;
      seen.add(s);
      urls.push(s);
    };
    for (const b of beads || []) {
      add(b.image_url);
      add(b.thumb_url);
      for (const u of beadDisplayImagePool(b)) add(u);
    }
    return urls;
  }

  function stubBeadFromCompact(handle, compact, index) {
    const price =
      compact?.p != null ? Number(compact.p) / 100 : Number(compact?.price || 0) || 0;
    const loose = resolveBeadLoose(handle);
    if (loose) {
      return beadWithRandomDisplayImage({
        ...loose,
        price: price || loose.price,
        instanceId: `${handle}-${index}-${Date.now()}`,
      });
    }
    return {
      handle,
      name: humanizeHandle(handle),
      diameter_mm: diameterFromHandle(handle),
      price,
      color: '#c8c8d0',
      image_url: '',
      instanceId: `stub-${handle}-${index}-${Date.now()}`,
    };
  }

  function expandDesign(designJson) {
    const normalized = normalizeDesignJson(designJson);
    if (!normalized) return [];

    const raw = parseRawDesignJson(designJson);
    const compactList = Array.isArray(raw?.b) ? raw.b : [];
    const beads = [];
    const list = normalized.beads || [];

    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      const compact = compactList[i];
      const meta = resolveBeadLoose(item.handle);
      if (meta) {
        beads.push(
          beadWithRandomDisplayImage({
            ...meta,
            price: compact?.p != null ? Number(compact.p) / 100 : meta.price,
            instanceId: `${item.handle}-${i}-${Date.now()}`,
          })
        );
        continue;
      }

      const replacement = findReplacementBead(item.handle, i);
      if (replacement) {
        beads.push(
          beadWithRandomDisplayImage({
            ...replacement,
            price: compact?.p != null ? Number(compact.p) / 100 : replacement.price,
            instanceId: `${replacement.handle}-${i}-${Date.now()}`,
          })
        );
        continue;
      }

      beads.push(stubBeadFromCompact(item.handle, compact, i));
    }
    return beads;
  }

  function beadLinePropertyKey(index) {
    const n = String(index).padStart(2, '0');
    return `_bead_${n}`;
  }

  /** Fallback when JSON is missing or handles no longer match catalog — uses _bead_01… (legacy: Bead #01). */
  function expandDesignFromLineProperties(properties) {
    if (!properties || typeof properties !== 'object') return [];

    const count = Number(properties['Bead Count'] || 0);
    if (!count) return [];

    const beads = [];
    for (let i = 1; i <= count; i++) {
      const legacyKey = i < 10 ? `Bead #0${i}` : `Bead #${i}`;
      const val = properties[beadLinePropertyKey(i)] || properties[legacyKey];
      if (!val) continue;

      const segments = String(val)
        .split(/[·•|]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const name = segments[0] || '';
      const diamMatch = segments[1]?.match(/(\d+(?:\.\d+)?)/);
      const diameter_mm = diamMatch ? Number(diamMatch[1]) : 8;

      let meta =
        BEADS().find(
          (b) =>
            b.enabled !== false &&
            String(b.name).toLowerCase() === name.toLowerCase() &&
            Number(b.diameter_mm) === diameter_mm
        ) ||
        BEADS().find(
          (b) => b.enabled !== false && String(b.name).toLowerCase() === name.toLowerCase()
        );

      if (!meta) {
        meta = {
          handle: `line-bead-${i}`,
          name: name || `Bead ${i}`,
          diameter_mm,
          price: 0,
          color: '#c8c8d0',
          image_url: '',
        };
      }

      beads.push(
        beadWithRandomDisplayImage({
          ...meta,
          instanceId: `cart-line-${i}-${Date.now()}`,
        })
      );
    }
    return beads;
  }

  function calcTotal(beads) {
    return beads.reduce((sum, b) => sum + Number(b.price || 0), 0);
  }

  const BRACELET_ID_KEY = '_bracelet_id';
  const BRACELET_ROLE_KEY = '_bracelet_role';

  /** Shopify variant id for a bead (catalog or expanded bead object). */
  function getBeadVariantId(bead) {
    const raw = bead?.variant_id ?? resolveBead(bead?.handle)?.variant_id;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /** Beads that cannot be added via priced variants. */
  function beadsMissingVariantIds(beads) {
    return beads.filter((b) => !getBeadVariantId(b));
  }

  /** Aggregate bead lines by variant for /cart/add.js items[]. */
  function aggregateBeadVariants(beads) {
    const groups = new Map();
    for (const b of beads) {
      const variantId = getBeadVariantId(b);
      if (!variantId) continue;
      const key = String(variantId);
      const prev = groups.get(key);
      if (prev) prev.quantity += 1;
      else groups.set(key, { variant_id: variantId, quantity: 1, handle: b.handle });
    }
    return Array.from(groups.values());
  }

  function buildBeadCartItems(beads, braceletId) {
    return aggregateBeadVariants(beads).map((group) =>
      applyLineItemProperties(
        { id: group.variant_id, quantity: group.quantity },
        {
          [BRACELET_ID_KEY]: braceletId,
          [BRACELET_ROLE_KEY]: 'bead',
        }
      )
    );
  }

  /** Flatten properties onto a cart line item (chunks values > 255 chars). */
  function applyLineItemProperties(item, properties) {
    const flat = item.properties ? { ...item.properties } : {};
    for (const [key, value] of Object.entries(properties)) {
      const str = String(value ?? '');
      if (str.length <= PROPERTY_VALUE_MAX) {
        flat[key] = str;
      } else {
        const chunks = Math.ceil(str.length / PROPERTY_VALUE_MAX);
        flat[`${key}_parts`] = String(chunks);
        for (let i = 0; i < chunks; i++) {
          flat[`${key}_${i}`] = str.slice(i * PROPERTY_VALUE_MAX, (i + 1) * PROPERTY_VALUE_MAX);
        }
      }
    }
    item.properties = flat;
    return item;
  }

  function firstBeadPreviewUrl(beads) {
    for (const b of beads || []) {
      const url = String(b.image_url || b.thumb_url || '').trim();
      if (url) return url.slice(0, PROPERTY_VALUE_MAX);
    }
    return '';
  }

  function buildAnchorLineProperties({ title, beads, wristRounded, designJson, designId, braceletId }) {
    const designStr = JSON.stringify(designJson);
    const totalCents = Math.round(calcTotal(beads) * 100);
    const summary = buildBomSummary(beads);
    const previewImage = firstBeadPreviewUrl(beads);
    const props = {
      'Design Title': (title || 'Custom Bracelet').slice(0, 240),
      'Design Summary': summary,
      'Wrist Size': `${formatWristCm(wristRounded)} cm`,
      'Bead Count': String(beads.length),
      _design_summary: summary,
      _bead_sequence: buildBeadSequence(beads).slice(0, PROPERTY_VALUE_MAX),
      _design_id: designId || crypto.randomUUID?.() || String(Date.now()),
      [BRACELET_ID_KEY]: braceletId,
      [BRACELET_ROLE_KEY]: 'anchor',
      _bracelet_total_cents: String(totalCents),
      _design_json: designStr,
    };
    if (previewImage) props._preview_image = previewImage;
    for (let i = 0; i < beads.length; i++) {
      const b = beads[i];
      const label = beadLinePropertyKey(i + 1);
      const price = Number(b.price || 0).toFixed(2);
      props[label] = `${b.name || b.handle} · ${b.diameter_mm || 8}mm · $${price}`.slice(0, PROPERTY_VALUE_MAX);
    }
    return props;
  }

  function calcCircumferenceCm(beads) {
    return beads.reduce((sum, b) => sum + (Number(b.diameter_mm) || 8) / 10, 0);
  }

  /** Round wrist/cm to 2 decimal places (avoids float noise in cart properties). */
  function roundWristCm(cm) {
    const n = Number(cm);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100) / 100;
  }

  /** Display cm with at most 2 decimal places (e.g. 16.4, not 16.400000000000006). */
  function formatWristCm(cm) {
    const n = roundWristCm(cm);
    return String(parseFloat(n.toFixed(2)));
  }

  /** Inches from cm (1 in = 2.54 cm), 2 decimal places. */
  function formatWristIn(cm) {
    const n = roundWristCm(cm);
    if (!n) return '0';
    return String(parseFloat((n / 2.54).toFixed(2)));
  }

  /** Designer / gallery display: "13.2cm / 5.20in" */
  function formatWristCmIn(cm) {
    return `${formatWristCm(cm)}cm / ${formatWristIn(cm)}in`;
  }

  /** Compact card label: "15.1cm/5.94in" */
  function formatWristCmInCompact(cm) {
    return `${formatWristCm(cm)}cm/${formatWristIn(cm)}in`;
  }

  function buildBomSummary(beads) {
    const counts = new Map();
    for (const b of beads) {
      counts.set(b.name, (counts.get(b.name) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, n]) => `${name}×${n}`)
      .join(', ')
      .slice(0, 240);
  }

  const PROPERTY_VALUE_MAX = 240;

  /** Ordered sequence for cart / orders (position 1 = first bead on bracelet, clockwise) */
  function buildBeadSequence(beads) {
    return beads.map((b, i) => `${i + 1}.${b.name || b.handle}`).join(' → ');
  }

  /** Per-bead line properties for fulfillment (underscore = hidden on storefront). */
  function appendBeadLineProperties(formData, beads) {
    for (let i = 0; i < beads.length; i++) {
      const b = beads[i];
      const label = beadLinePropertyKey(i + 1);
      const price = Number(b.price || 0).toFixed(2);
      const value = `${b.name || b.handle} · ${b.diameter_mm || 8}mm · $${price}`;
      formData.append(`properties[${label}]`, value.slice(0, PROPERTY_VALUE_MAX));
    }
  }

  function appendBeadSequenceProperties(formData, beads) {
    const full = buildBeadSequence(beads);
    appendLineItemProperty(formData, '_bead_sequence', full);
  }

  const DESIGN_JSON_KEYS = ['Bracelet Design', '_design_json'];

  function mergeChunkedProperty(properties, baseKey) {
    const direct = properties[baseKey];
    if (direct) return String(direct);

    const parts = Number(properties[`${baseKey}_parts`] ?? 0);
    if (!parts) return '';

    let merged = '';
    for (let i = 0; i < parts; i++) {
      merged += properties[`${baseKey}_${i}`] ?? '';
    }
    return merged;
  }

  /** Reassemble design JSON from cart line properties (public + private keys, chunked). */
  function parseDesignJsonFromProperties(properties) {
    if (!properties || typeof properties !== 'object') return null;

    for (const key of DESIGN_JSON_KEYS) {
      const raw = mergeChunkedProperty(properties, key);
      if (!raw) continue;
      try {
        return JSON.parse(raw);
      } catch {
        const normalized = normalizeDesignJson(raw);
        if (normalized) return normalized;
      }
    }
    return null;
  }

  /** Compact keys to reduce size; Shopify limits each property value to ~255 chars */
  function compactDesignJson(beads, wristSizeCm) {
    return {
      w: roundWristCm(wristSizeCm),
      b: beads.map((bead) => ({
        h: bead.handle,
        p: Math.round(Number(bead.price || 0) * 100),
      })),
    };
  }

  const DESIGN_CODE_PREFIX = 'CH1:';
  const SHORT_SHARE_PREFIX = 'CHDIY';
  const LOCAL_SHARE_KEY = 'bracelet_share_codes_v1';
  const LOCAL_SHARE_MAX = 200;

  function toBase64Url(str) {
    const bytes = new TextEncoder().encode(String(str));
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function fromBase64Url(b64) {
    const normalized = String(b64 || '')
      .trim()
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const pad = normalized.length % 4;
    const padded = pad ? normalized + '='.repeat(4 - pad) : normalized;
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }

  function buildDesignCodePayload({ title, wristSizeCm, beads, designJson } = {}) {
    const json = designJson || compactDesignJson(beads || [], wristSizeCm);
    const payload = {
      v: 1,
      w: json.w,
      b: json.b,
    };
    const label = String(title || '').trim();
    if (label) payload.t = label.slice(0, 80);
    return payload;
  }

  function normalizeDesignCodePayload(payload) {
    if (!payload || typeof payload !== 'object') return null;
    let beadList = payload.b;
    if (!Array.isArray(beadList) && Array.isArray(payload.beads)) {
      beadList = payload.beads.map((item) =>
        typeof item === 'string' ? { h: item } : { h: item?.h || item?.handle }
      );
    }
    if (!Array.isArray(beadList) || !beadList.length) return null;

    const designJson = {
      w: payload.w ?? payload.wrist_size_cm ?? 15,
      b: beadList
        .map((item) => {
          if (typeof item === 'string') return { h: item };
          const handle = item?.h || item?.handle;
          if (!handle) return null;
          const entry = { h: handle };
          if (item?.p != null) entry.p = Number(item.p);
          return entry;
        })
        .filter(Boolean),
    };
    if (!designJson.b.length) return null;

    return {
      title: String(payload.t || payload.title || '').trim(),
      designJson,
    };
  }

  /** Encode a bracelet design as a shareable copy/paste code. */
  function encodeDesignCode({ title, wristSizeCm, beads, designJson } = {}) {
    const payload = buildDesignCodePayload({ title, wristSizeCm, beads, designJson });
    if (!payload.b?.length) return '';
    return `${DESIGN_CODE_PREFIX}${toBase64Url(JSON.stringify(payload))}`;
  }

  /** Parse a pasted design code, JSON blob, or base64 payload. */
  function parseDesignCode(input) {
    if (input == null) return null;
    let raw = String(input).trim();
    if (!raw) return null;

    const tryPayload = (value) => {
      if (typeof value === 'string') {
        try {
          return normalizeDesignCodePayload(JSON.parse(value));
        } catch {
          return null;
        }
      }
      return normalizeDesignCodePayload(value);
    };

    const prefixMatch = raw.match(/^CH1:(.+)$/i);
    if (prefixMatch) {
      const decoded = tryPayload(fromBase64Url(prefixMatch[1]));
      if (decoded) return decoded;
    }

    const shortDigits = parseShortShareCode(raw);
    if (shortDigits) {
      const local = resolveLocalShareCode(shortDigits);
      if (local) return local;
    }

    const direct = tryPayload(raw);
    if (direct) return direct;

    try {
      const decoded = tryPayload(fromBase64Url(raw));
      if (decoded) return decoded;
    } catch {
      /* ignore */
    }

    return null;
  }

  function parseShortShareCode(input) {
    const raw = String(input || '').trim();
    if (!raw) return null;
    const prefixed = raw.match(/^CHDIY(\d{8})$/i) || raw.match(/^Design(\d{8})$/i);
    if (prefixed) return prefixed[1];
    const legacyCn = raw.match(/^[\u4e00-\u9fff]+(\d{8})$/);
    if (legacyCn) return legacyCn[1];
    const digitsOnly = raw.match(/^(\d{8})$/);
    return digitsOnly ? digitsOnly[1] : null;
  }

  function formatShareDisplayCode(code) {
    const digits = String(code || '')
      .replace(/\D/g, '')
      .slice(-8)
      .padStart(8, '0');
    return `${SHORT_SHARE_PREFIX}${digits}`;
  }

  function readLocalShareStore() {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_SHARE_KEY) || '{}') || {};
    } catch {
      return {};
    }
  }

  function writeLocalShareStore(store) {
    localStorage.setItem(LOCAL_SHARE_KEY, JSON.stringify(store));
  }

  function pruneLocalShareStore(store) {
    const keys = Object.keys(store);
    if (keys.length <= LOCAL_SHARE_MAX) return store;
    keys
      .sort((a, b) => (store[a]?.createdAt || 0) - (store[b]?.createdAt || 0))
      .slice(0, keys.length - LOCAL_SHARE_MAX)
      .forEach((key) => delete store[key]);
    return store;
  }

  function registerLocalShareCode({ title, wristSizeCm, beads, designJson, beadCount, price } = {}) {
    const payload = buildDesignCodePayload({ title, wristSizeCm, beads, designJson });
    if (!payload.b?.length) return null;
    let code = '';
    const store = readLocalShareStore();
    for (let i = 0; i < 8; i++) {
      const candidate = String(Math.floor(10000000 + Math.random() * 90000000));
      if (!store[candidate]) {
        code = candidate;
        break;
      }
    }
    if (!code) code = String(Date.now()).slice(-8).padStart(8, '0');
    store[code] = {
      title: String(title || '').trim(),
      wristSizeCm: payload.w,
      designJson: { w: payload.w, b: payload.b },
      beadCount: beadCount ?? payload.b.length,
      price: price ?? 0,
      createdAt: Date.now(),
    };
    writeLocalShareStore(pruneLocalShareStore(store));
    return { code, displayCode: formatShareDisplayCode(code) };
  }

  function resolveLocalShareCode(codeOrDigits) {
    const digits = parseShortShareCode(codeOrDigits) || String(codeOrDigits || '').replace(/\D/g, '').slice(0, 8);
    if (!digits || digits.length !== 8) return null;
    const entry = readLocalShareStore()[digits];
    if (!entry?.designJson) return null;
    return normalizeDesignCodePayload({
      t: entry.title,
      w: entry.wristSizeCm ?? entry.designJson?.w,
      b: entry.designJson?.b,
    });
  }

  const DESIGN_CLONE_KEY = 'bracelet_design_clone_v1';
  const DESIGN_CONFIRM_KEY = 'bracelet_design_confirm_v1';
  const MY_DESIGNS_STORAGE_PREFIX = 'bracelet_my_designs_v1';
  const MY_DESIGNS_GUEST_KEY = `${MY_DESIGNS_STORAGE_PREFIX}:guest`;
  const RESOLVED_CUSTOMER_ID_KEY = 'bracelet_resolved_customer_id_v1';
  /** Pass `false` to read/write the guest-only bucket (never the logged-in customer bucket). */
  const MY_DESIGNS_GUEST_ONLY = false;

  let resolvedCustomerId = null;

  function newDesignId() {
    return global.crypto?.randomUUID?.() || `design-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  function normalizeEmailKey(email) {
    return String(email || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9@._+-]/g, '');
  }

  function readAnalyticsCustomerId() {
    try {
      const cid = global.ShopifyAnalytics?.meta?.page?.customerId;
      if (cid && String(cid) !== '0') return String(cid);
    } catch {
      /* ignore */
    }
    try {
      const cid = global.meta?.page?.customerId;
      if (cid && String(cid) !== '0') return String(cid);
    } catch {
      /* ignore */
    }
    return null;
  }

  function getEffectiveCustomerId() {
    if (resolvedCustomerId) return resolvedCustomerId;
    const cfg = CONFIG();
    if (cfg.customerId) return String(cfg.customerId);
    try {
      const cached = localStorage.getItem(RESOLVED_CUSTOMER_ID_KEY);
      if (cached) return cached;
    } catch {
      /* ignore */
    }
    return readAnalyticsCustomerId();
  }

  function isLoggedInCustomer() {
    if (CONFIG().customerId || CONFIG().customerEmail) return true;
    const id = getEffectiveCustomerId();
    return !!id;
  }

  /**
   * Console snapshot for login debugging (design page: ?debug_auth=1 or BraceletCore.debugAuth()).
   */
  function debugAuth(label = 'BraceletDesigner') {
    const cfg = CONFIG();
    let cachedResolved = null;
    try {
      cachedResolved = localStorage.getItem(RESOLVED_CUSTOMER_ID_KEY);
    } catch {
      /* ignore */
    }
    const analyticsId = readAnalyticsCustomerId();
    const loggedIn = isLoggedInCustomer();
    const snapshot = {
      loggedIn,
      effectiveCustomerId: getEffectiveCustomerId(),
      myDesignsStorageKey: myDesignsStorageKey(),
      sources: {
        liquid_customerId: cfg.customerId ?? null,
        liquid_customerEmail: cfg.customerEmail ?? null,
        resolvedCustomerId_inMemory: resolvedCustomerId,
        localStorage_resolvedId: cachedResolved,
        shopifyAnalytics_customerId: (() => {
          try {
            const cid = global.ShopifyAnalytics?.meta?.page?.customerId;
            return cid && String(cid) !== '0' ? String(cid) : null;
          } catch {
            return null;
          }
        })(),
        meta_page_customerId: (() => {
          try {
            const cid = global.meta?.page?.customerId;
            return cid && String(cid) !== '0' ? String(cid) : null;
          } catch {
            return null;
          }
        })(),
        analytics_combined: analyticsId,
      },
      cloud: {
        designApiBaseUrl: cfg.designApiBaseUrl || '',
        cloudApiEnabled: !!global.DesignCloudApi?.enabled?.(),
      },
      storefrontLoginUrl: cfg.storefrontLoginUrl || null,
      howToRead: loggedIn
        ? 'loggedIn=true — Save/云端会认当前 effectiveCustomerId'
        : 'loggedIn=false — 游客；Liquid 与 Analytics 都无 customerId',
    };

    const summary = `[${label}] 登录态 loggedIn=${loggedIn} | customerId=${snapshot.effectiveCustomerId || '(无)'} | storage=${snapshot.myDesignsStorageKey}`;
    console.warn(summary, snapshot);
    console.groupCollapsed(`${summary} — 展开看明细`);
    console.table(snapshot.sources);
    console.groupEnd();
    return snapshot;
  }

  /** Resolve storefront customer id (Liquid + analytics fallback for new accounts). */
  async function ensureCustomerContext() {
    const cfg = CONFIG();
    if (cfg.customerId) {
      resolvedCustomerId = String(cfg.customerId);
      try {
        localStorage.setItem(RESOLVED_CUSTOMER_ID_KEY, resolvedCustomerId);
      } catch {
        /* ignore */
      }
      return resolvedCustomerId;
    }

    const analyticsId = readAnalyticsCustomerId();
    if (analyticsId) {
      resolvedCustomerId = analyticsId;
      try {
        localStorage.setItem(RESOLVED_CUSTOMER_ID_KEY, resolvedCustomerId);
      } catch {
        /* ignore */
      }
      return resolvedCustomerId;
    }

    try {
      const cached = localStorage.getItem(RESOLVED_CUSTOMER_ID_KEY);
      if (cached) {
        resolvedCustomerId = cached;
        return resolvedCustomerId;
      }
    } catch {
      /* ignore */
    }

    return null;
  }

  function myDesignsOwnerKey(explicitCustomerId) {
    if (explicitCustomerId === MY_DESIGNS_GUEST_ONLY) return 'guest';
    if (explicitCustomerId != null && explicitCustomerId !== '') {
      return `customer_${explicitCustomerId}`;
    }
    const id = getEffectiveCustomerId();
    if (id) return `customer_${id}`;
    const email = CONFIG().customerEmail;
    if (email) return `email_${normalizeEmailKey(email)}`;
    return 'guest';
  }

  function myDesignsStorageKey(explicitCustomerId) {
    if (explicitCustomerId === MY_DESIGNS_GUEST_ONLY) return MY_DESIGNS_GUEST_KEY;
    return `${MY_DESIGNS_STORAGE_PREFIX}:${myDesignsOwnerKey(explicitCustomerId)}`;
  }

  function readMyDesignsStore(explicitCustomerId) {
    try {
      const raw = localStorage.getItem(myDesignsStorageKey(explicitCustomerId));
      if (!raw) return { version: 1, designs: [] };
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.designs)) return { version: 1, designs: [] };
      return parsed;
    } catch {
      return { version: 1, designs: [] };
    }
  }

  function writeMyDesignsStore(designs, explicitCustomerId) {
    localStorage.setItem(
      myDesignsStorageKey(explicitCustomerId),
      JSON.stringify({
        version: 1,
        designs: designs || [],
      })
    );
  }

  function clearGuestMyDesignsStore() {
    try {
      localStorage.removeItem(MY_DESIGNS_GUEST_KEY);
    } catch {
      /* ignore */
    }
  }

  function inferDesignKind(designJson) {
    const count = designJson?.b?.length ?? designJson?.beads?.length ?? 0;
    return count <= 1 ? 'single' : 'bracelet';
  }

  function listMyDesigns() {
    return readMyDesignsStore()
      .designs.slice()
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  }

  function getMyDesign(id) {
    if (!id) return null;
    return listMyDesigns().find((d) => d.id === id) || null;
  }

  function saveMyDesign({ id, title, wristSizeCm, designJson, kind } = {}) {
    if (!designJson) throw new Error('Design data is required');
    const store = readMyDesignsStore();
    const now = new Date().toISOString();
    const resolvedId = id || newDesignId();
    const beadCount = designJson?.b?.length ?? 0;
    const entry = {
      id: resolvedId,
      title: String(title || 'My Crystal Bracelet').trim() || 'My Crystal Bracelet',
      wristSizeCm: Number(wristSizeCm) || 15,
      designJson,
      beadCount,
      kind: kind || inferDesignKind(designJson),
      createdAt: now,
      updatedAt: now,
    };

    const idx = store.designs.findIndex((d) => d.id === resolvedId);
    if (idx >= 0) {
      entry.createdAt = store.designs[idx].createdAt || now;
      store.designs[idx] = { ...store.designs[idx], ...entry, updatedAt: now };
    } else {
      store.designs.push(entry);
    }

    writeMyDesignsStore(store.designs);
    return getMyDesign(resolvedId);
  }

  function deleteMyDesign(id) {
    if (!id) return false;
    const store = readMyDesignsStore();
    const next = store.designs.filter((d) => d.id !== id);
    if (next.length === store.designs.length) return false;
    writeMyDesignsStore(next);
    return true;
  }

  /**
   * Move all guest-bucket designs into the logged-in customer bucket, then delete the guest bucket.
   * Runs whenever guest storage is non-empty and a customer id is known.
   */
  async function mergeGuestMyDesignsIfNeeded() {
    await ensureCustomerContext();
    const customerId = getEffectiveCustomerId();
    if (!customerId) return { merged: 0, cleared: false };

    const guestDesigns = readMyDesignsStore(MY_DESIGNS_GUEST_ONLY).designs;
    if (!guestDesigns.length) return { merged: 0, cleared: false };

    const customerStore = readMyDesignsStore(customerId);
    const byId = new Map(customerStore.designs.map((d) => [d.id, d]));
    let merged = 0;

    for (const design of guestDesigns) {
      if (byId.has(design.id)) {
        const existing = byId.get(design.id);
        const guestTime = new Date(design.updatedAt || 0).getTime();
        const existingTime = new Date(existing.updatedAt || 0).getTime();
        if (guestTime > existingTime) {
          byId.set(design.id, { ...existing, ...design, updatedAt: design.updatedAt });
        }
        continue;
      }
      byId.set(design.id, design);
      merged += 1;
    }

    writeMyDesignsStore(Array.from(byId.values()), customerId);
    clearGuestMyDesignsStore();
    return { merged, cleared: true, total: byId.size };
  }

  function designerUrlForDesign(designId) {
    const cfg = CONFIG();
    const base = cfg.designerUrl || '/pages/designer';
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}design=${encodeURIComponent(designId)}`;
  }

  /** Save design for Design Studio (preserves bead order). */
  function saveDesignClone({ title, wristSizeCm, beads, designJson }) {
    const payload = {
      title: title || 'Custom Bracelet',
      wristSizeCm,
      designJson: designJson || compactDesignJson(beads || [], wristSizeCm),
    };
    sessionStorage.setItem(DESIGN_CLONE_KEY, JSON.stringify(payload));
    return payload;
  }

  /** Persist a custom design for the product confirmation page. */
  function saveDesignConfirm({ title, wristSizeCm, beads, designJson, designId, createdAt }) {
    const existing = getDesignConfirm();
    const nextDesignId =
      designId != null && String(designId).trim() !== ''
        ? String(designId).trim()
        : existing?.designId || `custom-${Date.now()}`;
    const payload = {
      title: title != null ? String(title).trim() : '',
      wristSizeCm,
      designJson: designJson || compactDesignJson(beads || [], wristSizeCm),
      designId: nextDesignId,
      createdAt: createdAt || existing?.createdAt || new Date().toISOString(),
    };
    sessionStorage.setItem(DESIGN_CONFIRM_KEY, JSON.stringify(payload));
    return payload;
  }

  function getDesignConfirm() {
    try {
      const raw = sessionStorage.getItem(DESIGN_CONFIRM_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function clearDesignConfirm() {
    sessionStorage.removeItem(DESIGN_CONFIRM_KEY);
  }

  /** Read and clear clone payload from sessionStorage. */
  function consumeDesignClone() {
    try {
      const raw = sessionStorage.getItem(DESIGN_CLONE_KEY);
      if (!raw) return null;
      sessionStorage.removeItem(DESIGN_CLONE_KEY);
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  function appendLineItemProperty(formData, key, value) {
    const str = String(value ?? '');
    if (str.length <= PROPERTY_VALUE_MAX) {
      formData.append(`properties[${key}]`, str);
      return;
    }
    const chunks = Math.ceil(str.length / PROPERTY_VALUE_MAX);
    formData.append(`properties[${key}_parts]`, String(chunks));
    for (let i = 0; i < chunks; i++) {
      formData.append(
        `properties[${key}_${i}]`,
        str.slice(i * PROPERTY_VALUE_MAX, (i + 1) * PROPERTY_VALUE_MAX)
      );
    }
  }

  function parseCartError(data, status, rawText) {
    const body = rawText || '';
    if (/verified before you can proceed|Enable JavaScript and cookies/i.test(body)) {
      return (
        'Shopify blocked the cart request (security check). ' +
        'Test on your store URL (crystalhunter1.myshopify.com preview link), not localhost — then refresh and try again.'
      );
    }
    if (status === 403) {
      return (
        'Cart blocked (403). Use the myshopify.com theme preview URL instead of 127.0.0.1, log into Admin in the same browser, and disable VPN.'
      );
    }
    if (data?.description && data?.message) return `${data.description}: ${data.message}`;
    if (data?.description) return data.description;
    if (data?.message) return data.message;
    if (typeof data?.errors === 'string') return data.errors;
    if (body.length < 200 && !body.includes('<!DOCTYPE')) return body;
    return `Request failed (${status}). Check product is published and in stock.`;
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    return url.startsWith('//') ? `https:${url}` : url;
  }

  function loadImage(url) {
    return new Promise((resolve) => {
      if (!url) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = normalizeImageUrl(url);
    });
  }

  /** Theme logos may omit CORS headers; retry without crossOrigin for canvas draw. */
  function loadLogoImage(url) {
    return new Promise((resolve) => {
      if (!url) {
        resolve(null);
        return;
      }
      const src = normalizeImageUrl(url);
      const attempt = (useCors) =>
        new Promise((res) => {
          const img = new Image();
          if (useCors) img.crossOrigin = 'anonymous';
          img.onload = () => res(img);
          img.onerror = () => res(null);
          img.src = src;
        });
      attempt(true).then((img) => {
        if (img) resolve(img);
        else attempt(false).then(resolve);
      });
    });
  }

  /** Preload bead image URLs (canvas + thumbs) with limited concurrency. */
  async function preloadBeadImages(urls, opts = {}) {
    const unique = [...new Set((urls || []).filter(Boolean))];
    if (!unique.length) return { loaded: 0, total: 0 };

    const { concurrency = 6, imageCache, onProgress } = opts;
    let next = 0;
    let done = 0;
    const total = unique.length;

    const tick = () => onProgress?.(done, total);

    async function work() {
      while (next < total) {
        const url = unique[next++];
        if (imageCache?.has(url)) {
          done += 1;
          tick();
          continue;
        }
        const img = await loadImage(url);
        if (img && imageCache) imageCache.set(url, img);
        done += 1;
        tick();
      }
    }

    const workers = Math.min(Math.max(1, concurrency), total);
    await Promise.all(Array.from({ length: workers }, work));
    return { loaded: done, total };
  }

  function collectUniqueImageUrls(list, pickUrls) {
    const seen = new Set();
    const urls = [];
    const add = (u) => {
      const s = String(u || '').trim();
      if (!s || seen.has(s)) return;
      seen.add(s);
      urls.push(s);
    };
    for (const b of list) {
      for (const u of pickUrls(b) || []) add(u);
    }
    return urls;
  }

  /** Sidebar thumbnails only — small files, safe to gate first paint on. */
  function catalogThumbUrls(beads) {
    const list = beads || BEADS().filter((b) => b.enabled !== false);
    return collectUniqueImageUrls(list, (b) => {
      const thumb = resolveBeadThumbUrl(b);
      return thumb ? [thumb] : [];
    });
  }

  /** Canvas effect images (480px) — preload in background after studio opens. */
  function catalogCanvasImageUrls(beads) {
    const list = beads || BEADS().filter((b) => b.enabled !== false);
    return collectUniqueImageUrls(list, (b) => beadDisplayImagePool(b));
  }

  function catalogImageUrls(beads) {
    const list = beads || BEADS().filter((b) => b.enabled !== false);
    return collectUniqueImageUrls(list, (b) => {
      const urls = [];
      const thumb = resolveBeadThumbUrl(b);
      if (thumb) urls.push(thumb);
      for (const u of beadDisplayImagePool(b)) urls.push(u);
      return urls;
    });
  }

  function easeOutCubic(t) {
    return 1 - (1 - t) ** 3;
  }

  function easeOutQuart(t) {
    return 1 - (1 - t) ** 4;
  }

  function easeOutBack(t) {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
  }

  /** Quadratic bezier — arc path for bead fly-in. */
  function bezierPoint(from, to, control, t) {
    const u = 1 - t;
    return {
      x: u * u * from.x + 2 * u * t * control.x + t * t * to.x,
      y: u * u * from.y + 2 * u * t * control.y + t * t * to.y,
    };
  }

  function normalizeAngle(a) {
    let x = a % (Math.PI * 2);
    if (x < 0) x += Math.PI * 2;
    return x;
  }

  function angleDelta(from, to) {
    return Math.atan2(Math.sin(to - from), Math.cos(to - from));
  }

  function moveBeadInArray(beads, fromIndex, toIndex) {
    const arr = beads.slice();
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return arr;
    const [item] = arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, item);
    return arr;
  }

  function reorderBeadsByAngle(beads, dragFrom, pointerAngle, layout) {
    const n = beads.length;
    if (n <= 1 || dragFrom < 0 || dragFrom >= n) return beads.slice();

    const ref = -Math.PI / 2;
    const pa = normalizeAngle(pointerAngle - ref);
    let insertAt = 0;

    for (let i = 0; i < n; i++) {
      if (i === dragFrom) continue;
      const beadAngle = normalizeAngle(layout.slots[i].angle - ref);
      if (beadAngle < pa) insertAt++;
    }

    const next = beads.slice();
    const [item] = next.splice(dragFrom, 1);
    next.splice(insertAt, 0, item);
    return next;
  }

  function beadAngularHalfWidth(slot, baseR) {
    const r = (slot?.r ?? 8) * 1.12;
    return Math.min(r / Math.max(baseR, 1), Math.PI * 0.22);
  }

  function isAngleBetweenBeads(pa, a0, a1, wrap) {
    const eps = 0.0004;
    if (!wrap) {
      const span = normalizeAngle(a1 - a0);
      const t = normalizeAngle(pa - a0);
      return span > eps && t > eps && t < span - eps;
    }
    const t = pa < a0 ? normalizeAngle(pa + Math.PI * 2 - a0) : normalizeAngle(pa - a0);
    const span = normalizeAngle(a1 + Math.PI * 2 - a0);
    return span > eps && t > eps && t < span - eps;
  }

  function pointerGapAngle(dragFrom, pointerX, pointerY, layout) {
    const n = layout.slots.length;
    if (n <= 1 || dragFrom < 0 || dragFrom >= n) return null;

    const { cx, cy } = layoutRingCenter(layout, dragFrom);
    const baseR = layout.baseR;
    const proj = projectOntoCircle(pointerX, pointerY, cx, cy, baseR);
    const offRing = Math.hypot(pointerX - proj.x, pointerY - proj.y);
    const maxOffRing = Math.max(40, baseR * 0.58);
    if (offRing > maxOffRing) return null;

    const pa = proj.angle;
    const others = [];
    for (let i = 0; i < n; i++) {
      if (i === dragFrom) continue;
      others.push({ i, angle: layout.slots[i].angle });
    }
    if (others.length < 2) return null;
    others.sort((a, b) => a.angle - b.angle);

    for (let k = 0; k < others.length; k++) {
      const a0 = others[k].angle;
      const a1 = others[(k + 1) % others.length].angle;
      const wrap = k === others.length - 1;
      if (isAngleBetweenBeads(pa, a0, a1, wrap)) return proj.angle;
    }

    return null;
  }

  /** True when pointer sits in angular space between two other beads on the ring (not on a bead). */
  function isPointerInBeadGap(dragFrom, pointerX, pointerY, layout) {
    return pointerGapAngle(dragFrom, pointerX, pointerY, layout) != null;
  }

  function layoutRingCenter(layout, slotIndex = 0) {
    const slot = layout.slots[slotIndex] || layout.slots[0];
    if (!slot) return { cx: 0, cy: 0 };
    return {
      cx: slot.x - Math.cos(slot.angle) * layout.baseR,
      cy: slot.y - Math.sin(slot.angle) * layout.baseR,
    };
  }

  /** Reorder only when dropped in a gap between two beads; otherwise unchanged. */
  function reorderBeadsInGap(beads, dragFrom, pointerX, pointerY, layout) {
    const same = beads.slice();
    const pointerAngle = pointerGapAngle(dragFrom, pointerX, pointerY, layout);
    if (pointerAngle == null) {
      return { beads: same, changed: false };
    }
    const next = reorderBeadsByAngle(beads, dragFrom, pointerAngle, layout);
    let insertAt = 0;
    const ref = -Math.PI / 2;
    const pa = normalizeAngle(pointerAngle - ref);
    for (let i = 0; i < beads.length; i++) {
      if (i === dragFrom) continue;
      const beadAngle = normalizeAngle(layout.slots[i].angle - ref);
      if (beadAngle < pa) insertAt++;
    }
    const changed =
      insertAt !== dragFrom && next.some((b, i) => b.instanceId !== beads[i]?.instanceId);
    return { beads: next, changed };
  }

  function projectOntoCircle(x, y, cx, cy, baseR) {
    const angle = Math.atan2(y - cy, x - cx);
    return {
      angle,
      x: cx + Math.cos(angle) * baseR,
      y: cy + Math.sin(angle) * baseR,
    };
  }

  function beadRadiusPx(bead, size) {
    const d = Number(bead.diameter_mm) || 8;
    return (d / 8) * (size * 0.048);
  }

  /** Pixels per cm on canvas — kept in sync with beadRadiusPx (8mm → size×0.096 diameter). */
  function circumferencePxFromCm(cm, size) {
    return Math.max(0, Number(cm) || 0) * size * 0.12;
  }

  /** Soft ground shadow under each bead (fixed top-left light, myastris-style). */
  const BEAD_DROP_SHADOW = {
    peakAlpha: 0.78,
    spillAlpha: 0.36,
    spreadRatio: 0.42,
    offsetXRatio: 0.1,
    offsetYRatio: 0.08,
    liftRatio: 0.62,
    widthRatio: 0.88,
    heightRatio: 0.34,
  };

  /** Screen-space draw bounds per bead (body + ground shadow). */
  function beadDrawMargins(drawR) {
    const s = BEAD_DROP_SHADOW;
    const spread = Math.max(2, drawR * s.spreadRatio);
    const rx = drawR * s.widthRatio + spread * 0.45;
    const ry = drawR * s.heightRatio + spread * 0.28;
    const shadowDown = drawR * (s.liftRatio + s.offsetYRatio) + ry;
    const shadowRight = drawR * s.offsetXRatio + rx;
    const bleed = spread * 0.08;
    return {
      top: drawR + bleed,
      bottom: Math.max(drawR, shadowDown) + bleed,
      left: drawR + bleed,
      right: Math.max(drawR, shadowRight) + bleed,
    };
  }

  /** Keep ring + bead drawing inside the square canvas (shadow + stroke margin). */
  function ringRadiusCap(size, radii, maxRRatio) {
    const minR = size * 0.22;
    const maxBeadR = radii.length ? Math.max(...radii) : size * 0.055;
    const m = beadDrawMargins(maxBeadR);
    const maxExtent = Math.max(m.top, m.bottom, m.left, m.right);
    const edgePad = Math.max(size * 0.06, 14);
    const fromEdge = size / 2 - maxExtent - edgePad;
    const fromRatio = size * maxRRatio;
    return Math.max(minR, Math.min(fromRatio, fromEdge));
  }

  /**
   * Place beads on a circle along arc length totalArc.
   * R = totalArc / (2π); each step advances angle by arcStep / R so the loop closes at 2π.
   * If the ideal radius exceeds the canvas, bead radii scale down uniformly (no overlap).
   */
  function layoutBeadsOnCircle(beads, size, cx, cy, targetCircumferenceCm, opts = {}) {
    const n = beads.length;
    const displayScale = opts.beadScale ?? 1;
    const maxRRatio = opts.maxRRatio ?? 0.47;
    if (!n) return { baseR: size * 0.34, slots: [], cx, cy };

    const minR = size * 0.22;
    const targetArcPx = circumferencePxFromCm(targetCircumferenceCm ?? 15, size);

    function buildLayout(beadScale, arcTargetPx) {
      const arcPx = arcTargetPx ?? targetArcPx;
      const radii = beads.map((b) => beadRadiusPx(b, size) * displayScale * beadScale);
      const maxR = ringRadiusCap(size, radii, maxRRatio);
      const sumDiameters = radii.reduce((sum, r) => sum + r * 2, 0);
      const packed = sumDiameters >= arcPx;
      const gapArc = packed ? 0 : (arcPx - sumDiameters) / n;
      const totalArc = packed ? sumDiameters + n * gapArc : arcPx;

      const idealR = totalArc / (Math.PI * 2);
      const baseR = Math.max(minR, Math.min(maxR, idealR));

      let angle = -Math.PI / 2;
      const slots = [];
      for (let i = 0; i < n; i++) {
        const r = radii[i];
        slots.push({
          bead: beads[i],
          angle,
          x: cx + Math.cos(angle) * baseR,
          y: cy + Math.sin(angle) * baseR,
          r,
          scale: 1,
          opacity: 1,
        });
        const next = (i + 1) % n;
        const arcStep = radii[i] + gapArc + radii[next];
        angle += arcStep / baseR;
      }
      return { baseR, slots, idealR, beadScale, maxR };
    }

    let layout = buildLayout(1);
    if (layout.idealR > layout.maxR) {
      const canvasArcPx = layout.maxR * Math.PI * 2;
      layout = buildLayout(1, Math.min(targetArcPx, canvasArcPx));
    }
    if (layout.idealR > layout.maxR) {
      const shrink = layout.maxR / layout.idealR;
      layout = buildLayout(
        shrink,
        Math.min(targetArcPx, layout.maxR * Math.PI * 2)
      );
    }

    return { baseR: layout.baseR, slots: layout.slots, cx, cy };
  }

  function drawStageBackground(ctx, cx, cy, size) {
    /* No visible plate — page/container background shows through (white). */
  }

  /** Ring radius for empty bracelet or bead layout (from target wrist cm). */
  function ringBaseRadius(size, targetCircumferenceCm, opts = {}) {
    const maxRRatio = opts.maxRRatio ?? 0.47;
    const displayScale = opts.beadScale ?? 1;
    const targetArcPx = circumferencePxFromCm(targetCircumferenceCm ?? 15, size);
    const baseR = targetArcPx / (Math.PI * 2);
    const minR = size * 0.22;
    const estR = beadRadiusPx({ diameter_mm: 10 }, size) * displayScale;
    const maxR = ringRadiusCap(size, [estR], maxRRatio);
    return Math.max(minR, Math.min(maxR, baseR));
  }

  function drawRopeCircle(ctx, cx, cy, baseR, size, opts = {}) {
    const subtle = opts.subtle === true;
    const lw = Math.max(2.5, size * 0.008);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.strokeStyle = subtle ? '#ddd9d2' : '#c8c4bc';
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.arc(cx, cy, baseR, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = '#e8e6e1';
    ctx.lineWidth = Math.max(1.2, lw * 0.45);
    ctx.beginPath();
    ctx.arc(cx, cy, baseR - lw * 0.35, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(90, 85, 78, 0.22)';
    ctx.lineWidth = Math.max(0.8, lw * 0.25);
    ctx.setLineDash([size * 0.018, size * 0.014]);
    ctx.beginPath();
    ctx.arc(cx, cy, baseR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  function drawBrandWatermark(ctx, cx, cy, brandName) {
    if (!brandName) return;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(130, 115, 165, 0.28)';
    ctx.font = '600 15px system-ui, -apple-system, sans-serif';
    ctx.fillText(brandName, cx, cy - 6);
    ctx.fillStyle = 'rgba(130, 115, 165, 0.18)';
    ctx.font = '500 10px system-ui, -apple-system, sans-serif';
    ctx.fillText('DESIGN', cx, cy + 12);
    ctx.restore();
  }

  function drawCenterBrand(ctx, cx, cy, size, logoImg) {
    if (!logoImg) return;
    ctx.save();
    ctx.globalAlpha = 0.4;
    const zoneR = size * 0.12;
    const aspect = logoImg.width / logoImg.height;
    const wide = aspect > 2.2;
    const maxW = zoneR * (wide ? 2.6 : 1.65);
    const maxH = zoneR * (wide ? 1.12 : 1.1);
    let w = maxW;
    let h = w / aspect;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }
    ctx.drawImage(logoImg, cx - w / 2, cy - h / 2, w, h);
    ctx.restore();
  }

  /** Bead PNGs use a horizontal drill hole; rotate to the circle tangent (clockwise layout). */
  const BEAD_HOLE_AXIS_OFFSET = Math.PI / 2;

  function lerpAngle(from, to, t) {
    if (!Number.isFinite(from)) return to;
    if (!Number.isFinite(to)) return from;
    const diff = Math.atan2(Math.sin(to - from), Math.cos(to - from));
    return from + diff * t;
  }

  function slotLayoutAngle(slot, cx, cy) {
    if (Number.isFinite(slot.angle)) return slot.angle;
    return Math.atan2(slot.y - cy, slot.x - cx);
  }

  function beadTangentRotation(slot, cx, cy) {
    const bead = slot.bead || {};
    const extraDeg = Number(bead.rotation_offset_deg);
    const extra =
      Number.isFinite(extraDeg) ? (extraDeg * Math.PI) / 180 : 0;
    return slotLayoutAngle(slot, cx, cy) + BEAD_HOLE_AXIS_OFFSET + extra;
  }

  function layoutPointToScreen(x, y, cx, cy, viewRotation) {
    if (!viewRotation) return { x, y };
    const dx = x - cx;
    const dy = y - cy;
    const cos = Math.cos(viewRotation);
    const sin = Math.sin(viewRotation);
    return {
      x: cx + dx * cos - dy * sin,
      y: cy + dx * sin + dy * cos,
    };
  }

  function drawBeadGroundShadow(ctx, slot, drawOpts = {}) {
    const { x, y, r, scale = 1, opacity = 1 } = slot;
    const drawR = r * scale;
    if (drawR <= 0.4 || opacity <= 0.02) return;

    const ringCx = drawOpts.cx ?? x;
    const ringCy = drawOpts.cy ?? y;
    const viewRotation = drawOpts.viewRotation ?? 0;
    const screen = layoutPointToScreen(x, y, ringCx, ringCy, viewRotation);
    const s = BEAD_DROP_SHADOW;
    const peak = s.peakAlpha * opacity;
    const spill = s.spillAlpha * opacity;
    const spread = Math.max(2, drawR * s.spreadRatio);

    const shadowX = screen.x + drawR * s.offsetXRatio;
    const shadowY = screen.y + drawR * (s.liftRatio + s.offsetYRatio);
    const rx = drawR * s.widthRatio + spread * 0.45;
    const ry = drawR * s.heightRatio + spread * 0.28;

    ctx.save();
    ctx.translate(shadowX, shadowY);
    ctx.scale(1, ry / rx);
    const g = ctx.createRadialGradient(0, 0, rx * 0.06, 0, 0, rx);
    g.addColorStop(0, `rgba(0,0,0,${peak})`);
    g.addColorStop(0.32, `rgba(0,0,0,${spill})`);
    g.addColorStop(0.62, `rgba(0,0,0,${spill * 0.5})`);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(0, 0, rx, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  function drawBeadShadow(ctx, x, y, drawR, opacity, ringCx, ringCy) {
    drawBeadGroundShadow(
      ctx,
      { x, y, r: drawR, scale: 1, opacity, angle: 0 },
      { cx: ringCx, cy: ringCy }
    );
  }

  function drawBeadGem(ctx, slot, imageCache, selected, drawOpts = {}) {
    const { x, y, r, bead, scale = 1, opacity = 1 } = slot;
    const drawR = r * scale;
    if (drawR <= 0.4 || opacity <= 0.02) return;

    const cx = drawOpts.cx ?? x;
    const cy = drawOpts.cy ?? y;
    const rot = beadTangentRotation(slot, cx, cy);

    ctx.save();
    ctx.globalAlpha = opacity;

    ctx.translate(x, y);
    ctx.rotate(rot);

    const img = bead.image_url ? imageCache.get(bead.image_url) : null;
    const px = Math.ceil(drawR * 2);
    if (img) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(0, 0, drawR, 0, Math.PI * 2);
      ctx.clip();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, -drawR, -drawR, px, px);
      ctx.restore();

      ctx.save();
      ctx.globalAlpha = opacity * 0.28;
      const shine = ctx.createRadialGradient(
        -drawR * 0.38,
        -drawR * 0.4,
        0,
        -drawR * 0.08,
        -drawR * 0.08,
        drawR * 1.2
      );
      shine.addColorStop(0, 'rgba(255,255,255,0.75)');
      shine.addColorStop(0.42, 'rgba(255,255,255,0.1)');
      shine.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = shine;
      ctx.beginPath();
      ctx.arc(0, 0, drawR, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else {
      const grad = ctx.createRadialGradient(
        -drawR * 0.3,
        -drawR * 0.35,
        0,
        0,
        0,
        drawR
      );
      const col = bead.color || '#c8c8d0';
      grad.addColorStop(0, lighten(col, 55));
      grad.addColorStop(0.5, col);
      grad.addColorStop(1, lighten(col, -20));

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(0, 0, drawR, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = opacity * 0.18;
    ctx.strokeStyle = 'rgba(255,255,255,0.28)';
    ctx.lineWidth = Math.max(0.45, drawR * 0.045);
    ctx.beginPath();
    ctx.arc(0, 0, drawR - ctx.lineWidth * 0.4, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();
  }

  function flyInStartPoint(toSlot, layout, cx, cy, flyInFrom) {
    if (flyInFrom && Number.isFinite(flyInFrom.x) && Number.isFinite(flyInFrom.y)) {
      return { x: flyInFrom.x, y: flyInFrom.y };
    }
    const angle = toSlot.angle ?? Math.atan2(toSlot.y - cy, toSlot.x - cx);
    const dist = layout.baseR * 1.55;
    return {
      x: cx + Math.cos(angle) * dist,
      y: cy + Math.sin(angle) * dist,
    };
  }

  function interpolateLayouts(fromLayout, toLayout, t, animOpts = {}) {
    const ease = easeOutCubic(t);
    const baseR = fromLayout.baseR + (toLayout.baseR - fromLayout.baseR) * ease;
    const fromById = new Map(fromLayout.slots.map((s) => [s.bead.instanceId, s]));
    const { flyInFrom, newBeadId, cx = 0, cy = 0 } = animOpts;
    const posEase = easeOutQuart(Math.min(1, t / 0.88));

    const slots = toLayout.slots.map((toSlot) => {
      const fromSlot = fromById.get(toSlot.bead.instanceId);
      const isNew = !fromSlot;
      const targetAngle =
        toSlot.angle ?? Math.atan2(toSlot.y - cy, toSlot.x - cx);
      const blendAngle = (from, to, progress) =>
        fromSlot && Number.isFinite(from) && Number.isFinite(to)
          ? lerpAngle(from, to, progress)
          : to;

      if (isNew) {
        const isFlying = !newBeadId || toSlot.bead.instanceId === newBeadId;
        if (isFlying) {
          const target = { x: toSlot.x, y: toSlot.y };
          const start = flyInStartPoint(toSlot, toLayout, cx, cy, flyInFrom);
          const lift = Math.min(72, toLayout.baseR * 0.42);
          const control = {
            x: (start.x + target.x) / 2,
            y: Math.min(start.y, target.y) - lift,
          };
          const pos = bezierPoint(start, target, control, posEase);
          const scaleT = Math.max(0, (t - 0.08) / 0.92);
          const startAngle = Math.atan2(start.y - cy, start.x - cx);
          return {
            ...toSlot,
            x: pos.x,
            y: pos.y,
            angle: lerpAngle(startAngle, targetAngle, posEase),
            r: toSlot.r,
            scale: 0.35 + easeOutBack(scaleT) * 0.65,
            opacity: Math.min(1, t * 2.8),
          };
        }
      }
      return {
        ...toSlot,
        x: fromSlot ? fromSlot.x + (toSlot.x - fromSlot.x) * ease : toSlot.x,
        y: fromSlot ? fromSlot.y + (toSlot.y - fromSlot.y) * ease : toSlot.y,
        angle: blendAngle(fromSlot?.angle, targetAngle, ease),
        r: toSlot.r,
        scale: isNew ? easeOutBack(t) : 1,
        opacity: isNew ? Math.min(1, t * 2) : 1,
      };
    });

    return { baseR, slots };
  }

  class BraceletCanvas {
    constructor(canvas) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.beads = [];
      this.zoom = 1;
      this.viewRotation = 0;
      this.dragState = null;
      this.selectedIndex = -1;
      this.imageCache = new Map();
      this.displayDpr = 2;
      this.brandName = '';
      this.centerLogoUrl = '';
      this.centerLogoImage = null;
      this.skipCenterBrand = false;
      this.onBeadClick = null;
      this.anim = null;
      this.rafId = 0;
      this.targetCircumferenceCm = 15;
      this.beadDisplayScale = 1;
      this.ringMaxRatio = 0.47;
    }

    async preloadCenterLogo() {
      this.centerLogoImage = null;
      if (!this.centerLogoUrl) {
        this.draw();
        return;
      }
      const cached = this.imageCache.get(this.centerLogoUrl);
      if (cached) {
        this.centerLogoImage = cached;
        this.draw();
        return;
      }
      const img = await loadLogoImage(this.centerLogoUrl);
      if (img) {
        this.imageCache.set(this.centerLogoUrl, img);
        this.centerLogoImage = img;
      }
      this.draw();
    }

    setViewRotation(rad, opts = {}) {
      this.applyViewTransform({ rotation: rad, skipDraw: opts.skipDraw });
    }

    /** Update CSS zoom and/or canvas rotation; zoom is GPU-only, rotation triggers one draw. */
    applyViewTransform(opts = {}) {
      const { zoom: z, rotation, skipDraw = false } = opts;
      let needsDraw = false;

      if (z !== undefined) {
        const next = Number.isFinite(z) && z > 0 ? z : 1;
        if (next !== this.zoom) {
          this.zoom = next;
          this.applyDisplayTransform();
        }
      }

      if (rotation !== undefined) {
        const next = Number.isFinite(rotation) ? rotation : 0;
        if (next !== this.viewRotation) {
          this.viewRotation = next;
          needsDraw = true;
        }
      }

      if (needsDraw && !skipDraw) this.draw();
    }

    getViewRotation() {
      return this.viewRotation;
    }

    setDragState(state) {
      this.dragState = state;
      this.draw();
    }

    clearDragState() {
      this.dragState = null;
      this.draw();
    }

    clientToLayoutPoint(clientX, clientY) {
      const rect = this.canvas.getBoundingClientRect();
      const layoutSize = this.getDrawSize();
      const displayW = rect.width || layoutSize;
      const displayH = rect.height || layoutSize;
      const px = displayW > 0 ? layoutSize / displayW : 1;
      const py = displayH > 0 ? layoutSize / displayH : px;
      const cx = layoutSize / 2;
      const cy = layoutSize / 2;
      let x = (clientX - rect.left) * px;
      let y = (clientY - rect.top) * py;
      x -= cx;
      y -= cy;
      const cos = Math.cos(-this.viewRotation);
      const sin = Math.sin(-this.viewRotation);
      const rx = x * cos - y * sin;
      const ry = x * sin + y * cos;
      return { x: rx + cx, y: ry + cy, size: layoutSize, cx, cy };
    }

    /** Map screen coords from canvas center — works above/below the square canvas in the wrap. */
    clientToLayoutPointFree(clientX, clientY) {
      const rect = this.canvas.getBoundingClientRect();
      const layoutSize = this.getDrawSize();
      const displayW = rect.width || layoutSize;
      const displayH = rect.height || layoutSize;
      const px = displayW > 0 ? layoutSize / displayW : 1;
      const py = displayH > 0 ? layoutSize / displayH : px;
      const cx = layoutSize / 2;
      const cy = layoutSize / 2;
      const screenCx = rect.left + displayW / 2;
      const screenCy = rect.top + displayH / 2;
      let x = (clientX - screenCx) * px;
      let y = (clientY - screenCy) * py;
      const cos = Math.cos(-this.viewRotation);
      const sin = Math.sin(-this.viewRotation);
      const rx = x * cos - y * sin;
      const ry = x * sin + y * cos;
      return { x: rx + cx, y: ry + cy, size: layoutSize, cx, cy };
    }

    layoutPointToCanvas(x, y) {
      const size = this.getDrawSize();
      const cx = size / 2;
      const cy = size / 2;
      let px = x - cx;
      let py = y - cy;
      const cos = Math.cos(this.viewRotation);
      const sin = Math.sin(this.viewRotation);
      return {
        x: px * cos - py * sin + cx,
        y: px * sin + py * cos + cy,
      };
    }

    layoutPointToClient(layoutX, layoutY) {
      const rect = this.canvas.getBoundingClientRect();
      const size = this.getDrawSize();
      const displayW = rect.width || size;
      const displayH = rect.height || size;
      const sx = displayW / size;
      const sy = displayH / size;
      const cp = this.layoutPointToCanvas(layoutX, layoutY);
      return {
        x: rect.left + cp.x * sx,
        y: rect.top + cp.y * sy,
        pxPerLayout: (sx + sy) / 2,
      };
    }

    applyDragToLayout(layout) {
      if (!this.dragState || this.dragState.index < 0) return layout;
      const { index, angle, x, y, scale = 1.12, opacity = 1 } = this.dragState;
      const slots = layout.slots.map((slot, i) => {
        if (i !== index) return slot;
        return {
          ...slot,
          angle,
          x,
          y,
          scale,
          opacity,
        };
      });
      return { ...layout, slots };
    }

    setTargetCircumferenceCm(cm) {
      const n = Number(cm);
      this.targetCircumferenceCm = Number.isFinite(n) && n > 0 ? n : 15;
    }

    layoutOpts() {
      return { beadScale: this.beadDisplayScale, maxRRatio: this.ringMaxRatio };
    }

    layoutFor(beads, size, cx, cy) {
      return layoutBeadsOnCircle(beads, size, cx, cy, this.targetCircumferenceCm, this.layoutOpts());
    }

    getDrawSize() {
      return this.canvas.width / this.displayDpr;
    }

    setBeads(beads, opts = {}) {
      const animate = opts.animate !== false;
      const prevBeads = this.beads;
      const changed =
        beads.length !== prevBeads.length ||
        beads.some((b, i) => b.instanceId !== prevBeads[i]?.instanceId);

      if (animate && changed) {
        this.startAnimation(prevBeads, beads, opts);
      } else {
        this.stopAnimation();
        this.beads = beads.slice();
        this.draw();
      }
    }

    stopAnimation() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = 0;
      }
      this.anim = null;
    }

    startAnimation(fromBeads, toBeads, opts = {}) {
      const size = this.getDrawSize();
      const cx = size / 2;
      const cy = size / 2;
      const adding = toBeads.length > fromBeads.length;
      const newBeadId = adding ? toBeads[toBeads.length - 1]?.instanceId : null;
      this.anim = {
        start: performance.now(),
        duration: adding ? 580 : 360,
        fromBeads: fromBeads.slice(),
        toBeads: toBeads.slice(),
        from: this.layoutFor(fromBeads, size, cx, cy),
        to: this.layoutFor(toBeads, size, cx, cy),
        flyInFrom: opts.flyInFrom || null,
        flyInFromOrigin: opts.flyInFrom ? { x: opts.flyInFrom.x, y: opts.flyInFrom.y } : null,
        newBeadId,
        cx,
        cy,
        animSize: size,
        onComplete: opts.onComplete || null,
      };
      this.beads = toBeads.slice();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.tick();
    }

    layoutAtProgress(t) {
      if (!this.anim) return null;
      if (t >= 1) return this.anim.to;
      return interpolateLayouts(this.anim.from, this.anim.to, t, {
        flyInFrom: this.anim.flyInFrom,
        newBeadId: this.anim.newBeadId,
        cx: this.anim.cx,
        cy: this.anim.cy,
      });
    }

    tick() {
      if (!this.anim) return;
      const elapsed = performance.now() - this.anim.start;
      const t = Math.min(1, elapsed / this.anim.duration);
      this.draw(this.layoutAtProgress(t));

      if (t < 1) {
        this.rafId = requestAnimationFrame(() => this.tick());
      } else {
        const onComplete = this.anim?.onComplete;
        this.stopAnimation();
        this.draw();
        if (typeof onComplete === 'function') onComplete();
      }
    }

    setZoom(z) {
      this.applyViewTransform({ zoom: z });
    }

    applyDisplayTransform() {
      this.canvas.style.setProperty('--bd-canvas-zoom', String(this.zoom || 1));
    }

    /** Max CSS zoom so beads + shadows stay inside the canvas wrap. */
    getLayoutFitZoom(padRatio = 0.06) {
      const size = this.getDrawSize();
      if (!this.beads.length || size <= 0) return 1;

      const cx = size / 2;
      const cy = size / 2;
      const layout = this.layoutFor(this.beads, size, cx, cy);
      if (!layout.slots.length) return 1;

      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      for (const slot of layout.slots) {
        const drawR = slot.r * (slot.scale ?? 1);
        const m = beadDrawMargins(drawR);
        minX = Math.min(minX, slot.x - m.left);
        maxX = Math.max(maxX, slot.x + m.right);
        minY = Math.min(minY, slot.y - m.top);
        maxY = Math.max(maxY, slot.y + m.bottom);
      }

      const contentW = maxX - minX;
      const contentH = maxY - minY;
      if (!Number.isFinite(contentW) || !Number.isFinite(contentH)) return 1;
      const avail = size * (1 - padRatio * 2);
      if (contentW <= 0 || contentH <= 0) return 1;
      return Math.min(1, avail / Math.max(contentW, contentH));
    }

    async preloadImages(beads) {
      await preloadBeadImages(beadImageUrlsForPreload(beads), {
        imageCache: this.imageCache,
        concurrency: 8,
      });
    }

    refreshAnimationLayouts() {
      if (!this.anim) return;
      const size = this.getDrawSize();
      const cx = size / 2;
      const cy = size / 2;
      const prevSize = this.anim.animSize || size;
      const scale = prevSize > 0 ? size / prevSize : 1;

      this.anim.from = this.layoutFor(this.anim.fromBeads, size, cx, cy);
      this.anim.to = this.layoutFor(this.anim.toBeads, size, cx, cy);
      this.anim.cx = cx;
      this.anim.cy = cy;
      this.anim.animSize = size;

      if (this.anim.flyInFromOrigin) {
        this.anim.flyInFrom = {
          x: this.anim.flyInFromOrigin.x * scale,
          y: this.anim.flyInFromOrigin.y * scale,
        };
        this.anim.flyInFromOrigin = { ...this.anim.flyInFrom };
      }
    }

    resize() {
      const parent = this.canvas.parentElement;
      if (!parent) return;
      const parentStyle = getComputedStyle(parent);
      const padX =
        (parseFloat(parentStyle.paddingLeft) || 0) + (parseFloat(parentStyle.paddingRight) || 0);
      const padY =
        (parseFloat(parentStyle.paddingTop) || 0) + (parseFloat(parentStyle.paddingBottom) || 0);
      let pw = Math.max(0, (parent.clientWidth || 200) - padX);
      let ph = Math.max(0, (parent.clientHeight || pw) - padY);

      const inCartPreview = !!this.canvas.closest('[data-cart-bracelet-preview]');
      const inDesigner = !!this.canvas.closest('#bracelet-designer-root');
      const toolbar = parent.querySelector('[data-bd-canvas-toolbar]');
      if (toolbar && toolbar.offsetParent !== null && !inDesigner) {
        const tbStyle = getComputedStyle(toolbar);
        if (tbStyle.display !== 'none' && tbStyle.visibility !== 'hidden') {
          if (tbStyle.position === 'absolute' || tbStyle.position === 'fixed') {
            const bottom = parseFloat(tbStyle.bottom) || 0;
            ph = Math.max(0, ph - toolbar.offsetHeight - bottom - 8);
          } else {
            ph = Math.max(
              0,
              ph -
                toolbar.offsetHeight -
                (parseFloat(tbStyle.marginTop) || 0) -
                (parseFloat(tbStyle.marginBottom) || 0) -
                4
            );
          }
        }
      }

      const minPx = inCartPreview ? 32 : 120;
      const maxCap = inCartPreview ? 480 : Infinity;
      const size = Math.max(minPx, Math.min(pw, ph, maxCap));
      const dpr = inCartPreview
        ? Math.min(window.devicePixelRatio || 1, 2)
        : Math.min(window.devicePixelRatio || 1, inDesigner ? 3 : 2);
      this.displayDpr = dpr;
      this.canvas.width = size * dpr;
      this.canvas.height = size * dpr;
      this.canvas.style.flex = 'none';
      this.canvas.style.width = `${size}px`;
      this.canvas.style.height = `${size}px`;
      this.canvas.style.maxWidth = `${size}px`;
      this.canvas.style.maxHeight = `${size}px`;
      this.canvas.style.aspectRatio = '1 / 1';
      this.applyDisplayTransform();
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (this.anim) {
        this.refreshAnimationLayouts();
        const elapsed = performance.now() - this.anim.start;
        const t = Math.min(1, elapsed / this.anim.duration);
        this.draw(this.layoutAtProgress(t));
        if (t < 1 && !this.rafId) {
          this.rafId = requestAnimationFrame(() => this.tick());
        }
        return;
      }
      this.draw();
    }

    draw(layoutOverride) {
      const size = this.getDrawSize();
      const ctx = this.ctx;
      const cx = size / 2;
      const cy = size / 2;
      const n = this.beads.length;

      ctx.clearRect(0, 0, size, size);
      drawStageBackground(ctx, cx, cy, size);

      if (!this.skipCenterBrand && this.centerLogoImage) {
        drawCenterBrand(ctx, cx, cy, size, this.centerLogoImage);
      }

      if (n === 0) {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(this.viewRotation);
        ctx.translate(-cx, -cy);
        const baseR = ringBaseRadius(size, this.targetCircumferenceCm, this.layoutOpts());
        drawRopeCircle(ctx, cx, cy, baseR, size);
        ctx.restore();
        return;
      }

      let layout = layoutOverride || this.layoutFor(this.beads, size, cx, cy);
      layout = this.applyDragToLayout(layout);

      const dragIdx = this.dragState?.index ?? -1;
      const gemOpts = { cx, cy, viewRotation: this.viewRotation };

      const paintGroundShadow = (slot) => {
        const slotOpacity = slot.opacity ?? 1;
        if (slotOpacity <= 0.02) return;
        drawBeadGroundShadow(ctx, slot, gemOpts);
      };

      layout.slots.forEach((slot, i) => {
        if (i === dragIdx) return;
        paintGroundShadow(slot);
      });
      if (dragIdx >= 0 && layout.slots[dragIdx]) {
        paintGroundShadow(layout.slots[dragIdx]);
      }

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(this.viewRotation);
      ctx.translate(-cx, -cy);

      drawRopeCircle(ctx, cx, cy, layout.baseR, size);

      layout.slots.forEach((slot, i) => {
        if (i === dragIdx) return;
        drawBeadGem(ctx, slot, this.imageCache, false, gemOpts);
      });
      if (dragIdx >= 0 && layout.slots[dragIdx]) {
        const dragOpacity = layout.slots[dragIdx].opacity ?? 1;
        if (dragOpacity > 0.02) {
          drawBeadGem(ctx, layout.slots[dragIdx], this.imageCache, false, gemOpts);
        }
      }

      ctx.restore();
    }

    hitTest(clientX, clientY) {
      const { x, y, size, cx, cy } = this.clientToLayoutPoint(clientX, clientY);
      if (this.beads.length === 0) return -1;

      const layout = this.applyDragToLayout(this.layoutFor(this.beads, size, cx, cy));

      for (let i = layout.slots.length - 1; i >= 0; i--) {
        const { x: bx, y: by, r, scale = 1 } = layout.slots[i];
        const hitR = r * scale + 6;
        if (Math.hypot(x - bx, y - by) <= hitR) return i;
      }
      return -1;
    }

    hitRing(clientX, clientY) {
      const { x, y, size, cx, cy } = this.clientToLayoutPoint(clientX, clientY);
      if (this.beads.length === 0) {
        const baseR = ringBaseRadius(size, this.targetCircumferenceCm);
        const dist = Math.hypot(x - cx, y - cy);
        return Math.abs(dist - baseR) <= Math.max(18, size * 0.05);
      }
      const layout = this.layoutFor(this.beads, size, cx, cy);
      const dist = Math.hypot(x - cx, y - cy);
      return Math.abs(dist - layout.baseR) <= Math.max(18, size * 0.05);
    }

    pointerOnCircle(clientX, clientY) {
      const { x, y, size, cx, cy } = this.clientToLayoutPoint(clientX, clientY);
      const layout =
        this.beads.length > 0
          ? this.layoutFor(this.beads, size, cx, cy)
          : { baseR: ringBaseRadius(size, this.targetCircumferenceCm, this.layoutOpts()) };
      return projectOntoCircle(x, y, cx, cy, layout.baseR);
    }
  }

  function lighten(hex, amount) {
    const c = hex.replace('#', '');
    if (c.length < 6) return hex;
    const num = parseInt(c.slice(0, 6), 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0xff) + amount;
    let b = (num & 0xff) + amount;
    r = Math.min(255, r);
    g = Math.min(255, g);
    b = Math.min(255, b);
    return `rgb(${r},${g},${b})`;
  }

  async function clearCart() {
    const root = (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '/';
    const prefix = root.endsWith('/') ? root : `${root}/`;
    const res = await fetch(`${prefix}cart/clear.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseCartError(null, res.status, text));
    }
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  }

  async function notifyCartAdded(sourceId = 'bracelet-design') {
    const root = (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '/';
    const prefix = root.endsWith('/') ? root : `${root}/`;
    let cart = null;

    try {
      const res = await fetch(`${prefix}cart.js`, { credentials: 'same-origin' });
      if (res.ok) cart = await res.json();
    } catch {
      /* ignore */
    }

    const itemCount = cart?.item_count;
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || undefined,
          sourceId,
          data: {
            source: 'bracelet-design',
            itemCount: itemCount ?? 0,
          },
        },
      })
    );

    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  async function addToCart({
    title,
    beads,
    wristSizeCm,
    designId,
    replaceCart = false,
    openDrawer = !replaceCart,
    sourceId = 'bracelet-design',
  }) {
    if (replaceCart) {
      await clearCart();
    }

    const cfg = CONFIG();
    const anchorVariantId = Number(cfg.customBraceletVariantId);
    if (!Number.isFinite(anchorVariantId) || anchorVariantId <= 0) {
      throw new Error(
        'Create product "Custom Crystal Bracelet" (handle: custom-crystal-bracelet) or set Variant ID in Theme settings → Bracelet Designer'
      );
    }

    if (!beads?.length) {
      throw new Error('Add at least one bead before checkout.');
    }

    const missing = beadsMissingVariantIds(beads);
    if (missing.length) {
      const names = [...new Set(missing.map((b) => b.name || b.handle))].slice(0, 4);
      const suffix = missing.length > 4 ? ` (+${missing.length - 4} more)` : '';
      throw new Error(
        `Some beads are not linked to Shopify variants (${names.join(', ')}${suffix}). ` +
          'Run: node scripts/sync-bead-variants-shopify.mjs'
      );
    }

    const wristRounded = roundWristCm(wristSizeCm);
    const designJson = compactDesignJson(beads, wristRounded);
    const braceletId = designId || crypto.randomUUID?.() || String(Date.now());

    const anchorItem = applyLineItemProperties(
      { id: anchorVariantId, quantity: 1 },
      buildAnchorLineProperties({ title, beads, wristRounded, designJson, designId, braceletId })
    );

    const beadItems = buildBeadCartItems(beads, braceletId);

    // Anchor holds design metadata; hidden bead variant lines carry real Shopify prices for checkout.
    const items = [anchorItem, ...beadItems];

    const root = (typeof window !== 'undefined' && window.Shopify?.routes?.root) || '/';
    const url =
      cfg.cartAddUrl ||
      global.Theme?.routes?.cart_add_url ||
      `${root.endsWith('/') ? root : `${root}/`}cart/add.js`;

    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    });

    let data;
    const text = await res.text();
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(parseCartError(null, res.status, text));
    }

    if (!res.ok || data.status) {
      throw new Error(parseCartError(data, res.status, text));
    }

    if (openDrawer) {
      await notifyCartAdded(sourceId);
    }
    return data;
  }

  function mergedTemplates() {
    const demo = global.__DESIGN_TEMPLATES_DEMO__ || [];
    const primary = global.__DESIGN_TEMPLATES__ || [];
    const map = new Map();
    for (const t of demo) {
      if (t?.handle) map.set(t.handle, t);
    }
    for (const t of primary) {
      if (t?.handle) map.set(t.handle, t);
    }
    return Array.from(map.values());
  }

  function TEMPLATES_LIST() {
    return mergedTemplates();
  }

  function templateHasBeads(tpl) {
    if (!tpl?.design_json) return false;
    return expandDesign(tpl.design_json).length > 0;
  }

  function buildCatalogShowcaseTemplate() {
    const catalog = BEADS().filter((b) => b.enabled !== false);
    const targetCount = 15;
    const beadRefs = [];
    if (catalog.length) {
      for (let i = 0; i < targetCount; i++) {
        beadRefs.push({ handle: catalog[i % catalog.length].handle });
      }
    }
    const wristSizeCm = 15;
    const design_json = { wrist_size_cm: wristSizeCm, beads: beadRefs };
    const expanded = expandDesign(design_json);
    return {
      handle: 'default-showcase',
      title: 'The Ice',
      price: calcTotal(expanded) || 21.5,
      wrist_size_cm: wristSizeCm,
      bead_count: expanded.length || targetCount,
      cover_image_url: 'https://assets.myastris.com/materials/real-image/FkC_real_1.jpg',
      cover_color: '#e8eef5',
      design_json,
    };
  }

  /**
   * Resolve inspiration template: URL handle → the-ice → any valid template → catalog showcase.
   */
  function resolveDesignTemplate(requestedHandle) {
    const list = TEMPLATES_LIST();
    const req = (requestedHandle || '').trim().toLowerCase();

    if (req) {
      const exact = list.find((t) => (t.handle || '').toLowerCase() === req);
      if (exact && templateHasBeads(exact)) {
        return { tpl: exact, handle: exact.handle, isFallback: false };
      }
    }

    const tryHandles = ['the-ice', 'celestial-dream', 'forest-calm'];
    for (const h of tryHandles) {
      if (h === req) continue;
      const found = list.find((t) => (t.handle || '').toLowerCase() === h);
      if (found && templateHasBeads(found)) {
        return { tpl: found, handle: found.handle, isFallback: false };
      }
    }

    const firstValid = list.find((t) => templateHasBeads(t));
    if (firstValid) {
      return { tpl: firstValid, handle: firstValid.handle, isFallback: false };
    }

    const built = buildCatalogShowcaseTemplate();
    return { tpl: built, handle: built.handle, isFallback: false };
  }

  function getTemplateByHandle(handle) {
    return TEMPLATES_LIST().find((t) => t.handle === handle) || null;
  }

  function getFeaturedTemplates() {
    const all = TEMPLATES_LIST();
    const featured = all.filter((t) => t.featured !== false);
    return featured.length ? featured.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) : all;
  }

  /** Pick a random inspiration template (avoids immediate repeat when possible). */
  function pickRandomInspireTemplate(excludeHandle) {
    const candidates = getFeaturedTemplates().filter((t) => templateHasBeads(t));
    if (!candidates.length) {
      const built = buildCatalogShowcaseTemplate();
      return templateHasBeads(built) ? built : null;
    }
    const ex = (excludeHandle || '').trim().toLowerCase();
    const pool = ex
      ? candidates.filter((t) => (t.handle || '').toLowerCase() !== ex)
      : candidates;
    const pickFrom = pool.length ? pool : candidates;
    return pickFrom[Math.floor(Math.random() * pickFrom.length)];
  }

  /** Bracelet circle preview for homepage / cards (loads bead images like Design Studio). */
  async function drawThumbnail(canvas, beads, bgColor, thumbOpts = {}) {
    const galleryCard = thumbOpts.galleryCard === true;
    const wristCm = Number(thumbOpts.wristCm ?? thumbOpts.targetCircumferenceCm) || 15;
    const size = canvas.width || 280;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cx = size / 2;
    const cy = size / 2;

    ctx.clearRect(0, 0, size, size);
    const fill = bgColor || (galleryCard ? '#f6f6f4' : null);
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, size, size);
    }
    if (!galleryCard) drawStageBackground(ctx, cx, cy, size);
    if (!beads.length) return;

    const imageCache = new Map();
    await preloadBeadImages(beadImageUrlsForPreload(beads), { imageCache, concurrency: 6 });

    const layout = layoutBeadsOnCircle(beads, size, cx, cy, wristCm, {
      maxRRatio: thumbOpts.maxRRatio ?? 0.46,
      beadScale: thumbOpts.beadScale ?? 1,
    });
    drawRopeCircle(ctx, cx, cy, layout.baseR, size, { subtle: galleryCard });
    const gemOpts = { cx, cy };
    layout.slots.forEach((slot) => drawBeadGroundShadow(ctx, slot, gemOpts));
    layout.slots.forEach((slot) =>
      drawBeadGem(ctx, slot, imageCache, false, gemOpts)
    );
  }

  global.BraceletCore = {
    CONFIG,
    BEADS,
    TEMPLATES,
    getBeadMap,
    resolveBead,
    resolveBeadLoose,
    beadEffectImagePool,
    beadDisplayImagePool,
    resolveBeadThumbUrl,
    beadWithRandomDisplayImage,
    beadImageUrlsForPreload,
    findReplacementBead,
    normalizeDesignJson,
    parseDesignJsonFromProperties,
    expandDesign,
    expandDesignFromLineProperties,
    calcTotal,
    getBeadVariantId,
    beadsMissingVariantIds,
    aggregateBeadVariants,
    BRACELET_ID_KEY,
    BRACELET_ROLE_KEY,
    calcCircumferenceCm,
    roundWristCm,
    formatWristCm,
    formatWristIn,
    formatWristCmIn,
    formatWristCmInCompact,
    buildBomSummary,
    buildBeadSequence,
    compactDesignJson,
    encodeDesignCode,
    parseDesignCode,
    parseShortShareCode,
    formatShareDisplayCode,
    registerLocalShareCode,
    resolveLocalShareCode,
    DESIGN_CODE_PREFIX,
    SHORT_SHARE_PREFIX,
    saveDesignClone,
    consumeDesignClone,
    saveDesignConfirm,
    getDesignConfirm,
    clearDesignConfirm,
    DESIGN_CLONE_KEY,
    DESIGN_CONFIRM_KEY,
    newDesignId,
    listMyDesigns,
    getMyDesign,
    saveMyDesign,
    deleteMyDesign,
    mergeGuestMyDesignsIfNeeded,
    MY_DESIGNS_GUEST_ONLY,
    clearGuestMyDesignsStore,
    designerUrlForDesign,
    inferDesignKind,
    ensureCustomerContext,
    getEffectiveCustomerId,
    isLoggedInCustomer,
    debugAuth,
    appendLineItemProperty,
    loadImage,
    preloadBeadImages,
    catalogImageUrls,
    catalogThumbUrls,
    catalogCanvasImageUrls,
    moveBeadInArray,
    reorderBeadsByAngle,
    reorderBeadsInGap,
    isPointerInBeadGap,
    projectOntoCircle,
    normalizeAngle,
    angleDelta,
    BraceletCanvas,
    clearCart,
    addToCart,
    getTemplateByHandle,
    resolveDesignTemplate,
    getFeaturedTemplates,
    pickRandomInspireTemplate,
    drawThumbnail,
  };
})(typeof window !== 'undefined' ? window : globalThis);
